
import React, { useState } from 'react';
import { Button } from './Button';
import { AlertTriangle, CheckCircle2, Hash, Calendar, Building2, Info, Loader2, ShieldCheck } from 'lucide-react';

interface Props {
    isOpen: boolean;
    projectName: string;
    projectNumber: string;
    currentNumber: number; 
    currentDate: string;
    onConfirm: (correctedNumber: number | null, correctedDate: string | null) => Promise<void>;
}

export const DataCorrectionModal: React.FC<Props> = ({ 
    isOpen, 
    projectName, 
    projectNumber, 
    currentNumber, 
    currentDate, 
    onConfirm 
}) => {
    const [inputNr, setInputNr] = useState<string>(currentNumber.toString());
    const [inputDate, setInputDate] = useState<string>(currentDate ? currentDate.split('T')[0] : new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSave = async () => {
        const nr = parseInt(inputNr);
        if (isNaN(nr) || nr < 0) {
            alert("Bitte eine gültige Berichtsnummer eingeben.");
            return;
        }
        if (!inputDate) {
            alert("Bitte ein Datum für die letzte Begehung wählen.");
            return;
        }

        setLoading(true);
        await onConfirm(nr, inputDate);
        setLoading(false);
    };

    const hasChanges = parseInt(inputNr) !== currentNumber;

    return (
        <div className="fixed inset-0 z-[999] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200 relative">
                <div className="bg-amber-500 p-6 flex items-start gap-4">
                    <div className="bg-white/20 p-3 rounded-full">
                        <ShieldCheck className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-white">Daten-Check</h2>
                        <p className="text-amber-100 text-sm font-medium mt-1">Einmalige Prüfung der Berichtsnummer</p>
                    </div>
                </div>

                <div className="p-8">
                    <div className="mb-6 p-4 bg-slate-900 rounded-2xl text-white shadow-inner">
                        <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-4 h-4 text-blue-400" />
                            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Projekt</span>
                        </div>
                        <h3 className="font-bold text-lg leading-tight truncate">{projectName}</h3>
                        <div className="text-xs font-mono text-blue-300 mt-1">Ref: {projectNumber}</div>
                    </div>

                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-8">
                        <p className="text-slate-700 text-sm leading-relaxed">
                            Wir synchronisieren Ihre Daten mit der Cloud. 
                            Stimmt <b>Bericht Nr. {currentNumber}</b> mit Ihrem Stand der <u>letzten Begehung</u> überein?
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-1.5">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Hash className="w-4 h-4 text-blue-500" /> Begehungsnummer der letzten Begehung
                            </label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    min="1"
                                    value={inputNr}
                                    onChange={(e) => setInputNr(e.target.value)}
                                    className={`w-full p-4 pl-5 border-2 rounded-2xl text-2xl font-black focus:ring-4 outline-none transition-all ${
                                        hasChanges 
                                        ? 'border-blue-500 text-blue-700 focus:ring-blue-500/10' 
                                        : 'border-slate-200 text-slate-800 focus:border-emerald-500 focus:ring-emerald-500/10'
                                    }`}
                                />
                                {hasChanges && (
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-200 animate-pulse">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span className="text-[11px] font-black uppercase">Anpassung</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-1.5">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" /> Datum der letzten Begehung
                            </label>
                            <input 
                                type="date"
                                value={inputDate}
                                onChange={(e) => setInputDate(e.target.value)}
                                className="w-full p-3 border border-slate-200 rounded-xl text-slate-700 font-bold focus:border-blue-500 outline-none transition-all bg-slate-50 focus:bg-white"
                            />
                        </div>
                    </div>

                    <div className="mt-10 flex flex-col gap-3">
                        <Button 
                            onClick={handleSave}
                            disabled={loading}
                            className={`w-full py-4 text-base font-black rounded-2xl transition-all shadow-lg ${
                                hasChanges 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200' 
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                            }`}
                            icon={loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <CheckCircle2 className="w-5 h-5"/>}
                        >
                            {hasChanges ? 'Berichtsnr. korrigieren' : 'Berichtsnr. bestätigen'}
                        </Button>
                        <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1">
                            <Info className="w-3 h-3" /> Alle System-Zähler werden mit diesem Wert synchronisiert.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
